/**
 * OffsetsTable.js
 *
 * This is the main component for displaying and managing offset data in a table format.
 * It provides functionality for viewing, editing, and deleting offset records.
 *
 * Key Features:
 * - Displays offset data in a paginated table
 * - Supports edit and delete modes
 * - Handles row selection for batch operations
 * - Provides refresh functionality
 * - Shows status messages and loading states
 * - Confirms deletions before execution
 *
 * Props:
 * @param {Object} TableConfig - Configuration object containing:
 *   - offsetTableTitle: Title of the table
 *   - offsetTableHeaders: Array of column headers
 *   - TableId: Identifier for the table type
 */

// Flags.js
import React, { useEffect, useState } from "react";
import { Container, Box, Typography, Button, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CustomTable from "./CustomTable";
import EditModeActions from "./EditModeActions";
import DeleteModeActions from "./DeleteModeActions";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import MessageDisplay from "../Form/MessageDisplay";
import CancelIcon from "@mui/icons-material/Cancel";
import { useOffsets } from "../../services/useOffsets";
import { deleteOffset } from "../../services/api";

/**
 * Main component for managing offset data
 * @param {Object} TableConfig - Configuration object for the table
 */
const OffsetsTable = ({ TableConfig }) => {
  const {
    data,
    isLoading,
    error,
    refetchRows,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useOffsets();
  console.log("data", data);
  const [editMode, setEditMode] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedRows, setselectedRows] = useState([]);
  const [cancelEditMode, setCancelEditMode] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [message, setMessage] = useState({
    text: "",
    severity: "success",
    open: false,
  });

  /**
   * Refreshes the table data
   * Calls the API to fetch the latest data
   */
  const handleRefresh = async () => {
    await refetchRows();
  };

  /**
   * Toggles edit mode on/off
   * Resets delete mode and selected rows when switching modes
   */
  const handleEditMode = () => {
    setEditMode(!editMode);
    setDeleteMode(false);
    setselectedRows([]);
  };

  /**
   * Toggles delete mode on/off
   * Resets edit mode and selected rows when switching modes
   */
  const handleDeleteMode = () => {
    setDeleteMode(!deleteMode);
    setEditMode(false);
    setselectedRows([]);
  };

  /**
   * Cancels the current mode (edit or delete)
   * Resets all mode-related states
   */
  const handleCancelMode = () => {
    setEditMode(false);
    setDeleteMode(false);
    setselectedRows([]);
    setCancelEditMode(true);
  };
  useEffect(() => {
    if (cancelEditMode) {
      setCancelEditMode(false);
    }
  }, [cancelEditMode]);

  /**
   * Closes the status message display
   */
  const handleCloseMessage = () => {
    setMessage((prev) => ({ ...prev, open: false }));
  };

  if (error) {
    return <Typography color="error">Error: {error.message}</Typography>;
  }

  return (
    <Container>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h5" sx={{ mr: 2 }}>
              {TableConfig.offsetTableTitle}
            </Typography>
            <IconButton
              onClick={handleRefresh}
              size="large"
              sx={{ p: 0 }}
              aria-label="refresh"
            >
              <RefreshIcon />
            </IconButton>
          </Box>
          <Box>
            {(editMode || deleteMode) && (
              <Button
                onClick={handleCancelMode}
                startIcon={<CancelIcon />}
                variant="outlined"
                size="small"
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
            )}

            <EditModeActions
              editMode={editMode}
              handleEditMode={handleEditMode}
            />
            <DeleteModeActions
              deleteMode={deleteMode}
              handleDeleteMode={handleDeleteMode}
              selectedRows={selectedRows}
              setDeleteConfirmOpen={setDeleteConfirmOpen}
              buttonText="Reset Offset"
            />
          </Box>
        </Box>
        <CustomTable
          dataArray={data ? data.pages.flatMap((page) => page.offsets) : []}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          editMode={editMode}
          deleteMode={deleteMode}
          selectedRows={selectedRows}
          setselectedRows={setselectedRows}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          refetchRows={refetchRows}
          setMessage={setMessage}
          tableHeaders={TableConfig.offsetTableHeaders}
          cancelEditMode={cancelEditMode}
          TableId={TableConfig.TableId}
        />
        <DeleteConfirmDialog
          open={deleteConfirmOpen}
          setOpen={setDeleteConfirmOpen}
          selectedRows={selectedRows}
          setselectedRows={setselectedRows}
          setDeleteMode={setDeleteMode}
          refetchRows={refetchRows}
          setMessage={setMessage}
          deleteFunction={deleteOffset}
          dialogTitle="Confirm Offset Deletion"
          dialogContent="Deleting this offset will reset all adjusted values for the field back to their original values. Do you want to proceed?"
          cancelButtonText="Cancel"
          confirmButtonText="Confirm"
        />
        <MessageDisplay
          message={isLoading ? "Processing..." : message.text}
          severity={isLoading ? "info" : message.severity}
          open={isLoading || message.open}
          onClose={handleCloseMessage}
        />
      </Box>
    </Container>
  );
};

export default OffsetsTable;
